<template>
  <div class="signature-maker">
    <div class="sig-tabs" role="tablist">
      <button
          v-for="tab in tabs"
          :key="tab.id"
          class="sig-tab"
          :class="{ 'sig-tab--active': activeTab === tab.id }"
          @click="switchTab(tab.id)"
          role="tab"
          :aria-selected="activeTab === tab.id"
          type="button"
      >
        <component :is="tab.iconComponent" size="16" class="sig-tab__icon" />
        {{ tab.label }}
      </button>
    </div>

    <div v-show="activeTab === 'draw'" class="sig-panel">
      <div class="sig-canvas-wrap">
        <canvas ref="drawCanvas" class="sig-canvas" :width="canvasWidth" :height="canvasHeight"></canvas>
        <div v-if="isDrawEmpty" class="sig-canvas-hint">Sign here using your mouse or touch</div>
      </div>

      <div class="sig-footer">
        <div class="sig-color-row">
          <span class="sig-color-label">Color</span>
          <button
              v-for="color in colors"
              :key="color"
              class="sig-color-dot"
              :class="{ 'sig-color-dot--active': drawColor === color }"
              :style="{ background: color }"
              @click="setDrawColor(color)"
              type="button"
          ></button>
        </div>
        <div class="sig-actions">
          <button class="sig-btn sig-btn--ghost" @click="clearDraw" type="button">
            <rotate-ccw-icon size="14" />
            Clear
          </button>
        </div>
      </div>
    </div>

    <div v-show="activeTab === 'type'" class="sig-panel">
      <div class="sig-type-controls">
        <input
            v-model="typedName"
            class="sig-type-input"
            placeholder="Type your name..."
            @input="onTypeInput"
            maxlength="60"
            type="text"
        />

        <div class="sig-font-dropdown" ref="fontDropdown">
          <button class="sig-font-trigger" type="button" @click.stop="fontDropdownOpen = !fontDropdownOpen">
            <span class="sig-font-trigger__preview" :style="{ fontFamily: selectedFontFamily }">Aa</span>
            <span class="sig-font-trigger__name">{{ selectedFont }}</span>
            <chevron-down-icon
                size="12"
                class="sig-font-trigger__caret"
                :class="{ 'sig-font-trigger__caret--open': fontDropdownOpen }"
            />
          </button>

          <transition name="sig-dropdown-fade">
            <ul v-if="fontDropdownOpen" class="sig-font-menu" role="listbox">
              <li
                  v-for="font in computedFontArray"
                  :key="font.key"
                  class="sig-font-option"
                  :class="{ 'sig-font-option--active': selectedFont === font.key }"
                  role="option"
                  :aria-selected="selectedFont === font.key"
                  @click="selectFontFromDropdown(font)"
              >
                <span class="sig-font-option__sample" :style="{ fontFamily: font.family }">Aa</span>
                <span class="sig-font-option__name">{{ font.key }}</span>
                <check-icon v-if="selectedFont === font.key" size="14" class="sig-font-option__check" />
              </li>
            </ul>
          </transition>
        </div>
      </div>

      <div class="sig-canvas-wrap">
        <canvas ref="typeCanvas" class="sig-canvas" :width="canvasWidth" :height="canvasHeight"></canvas>
        <div v-if="!typedName" class="sig-canvas-hint">Your signature preview will appear here</div>
      </div>

      <div class="sig-footer">
        <div class="sig-color-row">
          <span class="sig-color-label">Color</span>
          <button
              v-for="color in colors"
              :key="color"
              class="sig-color-dot"
              :class="{ 'sig-color-dot--active': typeColor === color }"
              :style="{ background: color }"
              @click="setTypeColor(color)"
              type="button"
          ></button>
        </div>
        <div class="sig-actions">
          <button class="sig-btn sig-btn--ghost" @click="clearType" type="button">
            <rotate-ccw-icon size="14" />
            Clear
          </button>
        </div>
      </div>
    </div>

    <div v-show="activeTab === 'upload'" class="sig-panel">
      <div
          class="sig-dropzone"
          :class="{ 'sig-dropzone--active': isDragOver, 'sig-dropzone--has-file': uploadPreview }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="handleDrop"
          @click="!uploadPreview && $refs.fileInput.click()"
      >
        <template v-if="!uploadPreview">
          <upload-cloud-icon size="36" class="sig-dropzone__icon" />
          <p class="sig-dropzone__text">Drop files here or <span class="sig-dropzone__link">Browse</span></p>
          <p class="sig-dropzone__hint">PNG, JPG, SVG accepted</p>
          <input
              ref="fileInput"
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              @change="handleFileSelect"
              style="display:none"
          />
        </template>
        <template v-else>
          <img :src="uploadPreview" class="sig-upload-preview" alt="Signature preview" />
          <button class="sig-upload-remove" @click.stop="clearUpload" type="button">
            <x-icon size="16" />
          </button>
        </template>
      </div>

      <div v-if="uploadError" class="sig-upload-error">{{ uploadError }}</div>

      <div class="sig-footer sig-footer--upload">
        <div class="sig-actions">
          <button class="sig-btn sig-btn--ghost" @click="clearUpload" type="button">
            <rotate-ccw-icon size="14" />
            Clear
          </button>
        </div>
      </div>
    </div>

    <div v-if="showError" class="sig-validation-error">
      <alert-circle-icon size="14" />
      Signature is required.
    </div>
  </div>
</template>

<script>
import {
  CrosshairIcon,
  TypeIcon,
  UploadIcon,
  RotateCcwIcon,
  ChevronDownIcon,
  CheckIcon,
  XIcon,
  AlertCircleIcon,
  UploadCloudIcon,
} from 'vue-feather-icons';

const DEFAULT_FONT_ARRAY = [
  { key: 'Dancing Script', value: 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap' },
  { key: 'Pacifico',       value: 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap' },
  { key: 'Satisfy',        value: 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap' },
  { key: 'Great Vibes',    value: 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap' },
  { key: 'Pinyon Script',  value: 'https://fonts.googleapis.com/css2?family=Pinyon+Script&display=swap' },
];

function toCssFontFamily(name) {
  return "'" + name + "', cursive";
}

export default {
  name: 'SignatureMaker',

  components: {
    CrosshairIcon,
    TypeIcon,
    UploadIcon,
    RotateCcwIcon,
    ChevronDownIcon,
    CheckIcon,
    XIcon,
    AlertCircleIcon,
    UploadCloudIcon,
  },

  props: {
    hasError:  { type: Boolean, default: false },
    fontArray: { type: Array,   default: () => [] },
  },

  data() {
    return {
      activeTab: 'draw',

      tabs: [
        { id: 'draw',   label: 'Draw',   iconComponent: 'CrosshairIcon' },
        { id: 'type',   label: 'Type',   iconComponent: 'TypeIcon' },
        { id: 'upload', label: 'Upload', iconComponent: 'UploadIcon' },
      ],

      colors: ['#1a1a1a', '#e53e3e', '#38a169', '#3182ce', '#dd6b20'],
      canvasWidth: 700,
      canvasHeight: 360,
      showError: false,
      typeDebounceTimer: null,

      // Draw tab
      drawColor: '#1a1a1a',
      isDrawing: false,
      isDrawEmpty: true,
      lastX: 0,
      lastY: 0,
      drawCtx: null,
      drawBase64: null,

      // Type tab
      typedName: '',
      typeColor: '#1a1a1a',
      typeBase64: null,
      selectedFont: '',
      selectedFontFamily: '',
      fontDropdownOpen: false,
      typeCtx: null,

      // Upload tab
      uploadPreview: null,
      uploadBase64: null,
      uploadFile: null,
      isDragOver: false,
      uploadError: '',
    };
  },

  computed: {
    computedFontArray() {
      const source = this.fontArray && this.fontArray.length ? this.fontArray : DEFAULT_FONT_ARRAY;
      return source.map(f => ({ key: f.key, value: f.value, family: toCssFontFamily(f.key) }));
    },

    activeSignatureBase64() {
      if (this.activeTab === 'draw')   return this.drawBase64;
      if (this.activeTab === 'type')   return this.typeBase64;
      if (this.activeTab === 'upload') return this.uploadBase64;
      return null;
    },

    activeSignatureType() {
      return this.activeSignatureBase64 ? this.activeTab : null;
    },

    activeSignatureFile() {
      return this.activeTab === 'upload' ? this.uploadFile : null;
    },
  },

  watch: {
    computedFontArray(newList) {
      this.loadFonts();
      if (newList.length) {
        this.selectedFont       = newList[0].key;
        this.selectedFontFamily = newList[0].family;
      }
    },
  },

  mounted() {
    this.loadFonts();
    this.initDefaultFont();
    this.$nextTick(() => {
      if (this.activeTab === 'draw') {
        this.initDrawCanvas();
      }
      if (this.activeTab === 'type') {
        this.initTypeCanvas();
        this.renderTypedSignature();
      }
    });
    window.addEventListener('resize', this.onResize);
    document.addEventListener('click', this.closeDropdownOutside);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('click', this.closeDropdownOutside);
    this.detachDrawListeners();
    if (this.typeDebounceTimer) clearTimeout(this.typeDebounceTimer);
  },

  methods: {

    switchTab(id) {
      if (this.activeTab === id) return;
      this.clearTabData(this.activeTab);
      this.activeTab = id;
      this.showError = false;
      this.fontDropdownOpen = false;
      this.$emit('change', null);
      this.$nextTick(() => {
        if (id === 'draw') this.initDrawCanvas();
        if (id === 'type') { this.initTypeCanvas(); this.renderTypedSignature(); }
      });
    },

    clearTabData(tabId) {
      if (tabId === 'draw') {
        if (this.drawCtx) {
          const c = this.$refs.drawCanvas;
          if (c) this.drawCtx.clearRect(0, 0, c.width, c.height);
        }
        this.isDrawEmpty = true;
        this.drawBase64  = null;
      } else if (tabId === 'type') {
        this.typedName  = '';
        this.typeBase64 = null;
        if (this.typeCtx) {
          const c = this.$refs.typeCanvas;
          if (c) this.typeCtx.clearRect(0, 0, c.width, c.height);
        }
      } else if (tabId === 'upload') {
        this.uploadPreview = null;
        this.uploadBase64  = null;
        this.uploadFile    = null;
        this.uploadError   = '';
      }
    },

    // draw tab
    initDrawCanvas() {
      const canvas = this.$refs.drawCanvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();

      canvas.width  = rect.width  || 700;
      canvas.height = rect.height || 260;

      this.drawCtx  = canvas.getContext('2d');
      this.drawCtx.strokeStyle = this.drawColor;
      this.drawCtx.lineWidth   = 1.7;
      this.drawCtx.lineCap     = 'round';
      this.drawCtx.lineJoin    = 'round';
      this.drawCtx.clearRect(0, 0, canvas.width, canvas.height);
      this.attachDrawListeners(canvas);
    },

    attachDrawListeners(canvas) {
      this.detachDrawListeners();
      this._drawStart = (e) => this.startDraw(e);
      this._drawMove  = (e) => this.draw(e);
      this._drawEnd   = ()  => { this.isDrawing = false; this.captureDrawBase64(); };
      canvas.addEventListener('mousedown',  this._drawStart);
      canvas.addEventListener('mousemove',  this._drawMove);
      canvas.addEventListener('mouseup',    this._drawEnd);
      canvas.addEventListener('mouseleave', this._drawEnd);
      canvas.addEventListener('touchstart', this._drawStart, { passive: false });
      canvas.addEventListener('touchmove',  this._drawMove,  { passive: false });
      canvas.addEventListener('touchend',   this._drawEnd);
    },

    detachDrawListeners() {
      const canvas = this.$refs.drawCanvas;
      if (!canvas || !this._drawStart) return;
      canvas.removeEventListener('mousedown',  this._drawStart);
      canvas.removeEventListener('mousemove',  this._drawMove);
      canvas.removeEventListener('mouseup',    this._drawEnd);
      canvas.removeEventListener('mouseleave', this._drawEnd);
      canvas.removeEventListener('touchstart', this._drawStart);
      canvas.removeEventListener('touchmove',  this._drawMove);
      canvas.removeEventListener('touchend',   this._drawEnd);
    },

    getPos(e, canvas) {
      const rect = canvas.getBoundingClientRect();
      const src  = e.touches ? e.touches[0] : e;
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const cssX = src.clientX - rect.left;
      const cssY = src.clientY - rect.top;
      return {
        x: cssX * scaleX,
        y: cssY * scaleY
      };
    },

    startDraw(e) {
      e.preventDefault();
      const pos = this.getPos(e, this.$refs.drawCanvas);
      this.isDrawing = true;
      this.isDrawEmpty = false;
      this.showError = false;
      this.lastX = pos.x;
      this.lastY = pos.y;
      this.drawCtx.beginPath();
      this.drawCtx.arc(pos.x, pos.y, 1.2, 0, Math.PI * 2);
      this.drawCtx.fillStyle = this.drawColor;
      this.drawCtx.fill();
    },

    draw(e) {
      if (!this.isDrawing) return;
      if (e.cancelable) e.preventDefault();
      const pos = this.getPos(e, this.$refs.drawCanvas);
      this.drawCtx.strokeStyle = this.drawColor;
      this.drawCtx.beginPath();
      this.drawCtx.moveTo(this.lastX, this.lastY);
      this.drawCtx.lineTo(pos.x, pos.y);
      this.drawCtx.stroke();
      this.lastX = pos.x;
      this.lastY = pos.y;
    },

    captureDrawBase64() {
      if (this.isDrawEmpty) return;
      const canvas = this.$refs.drawCanvas;
      if (!canvas) return;
      this.drawBase64 = canvas.toDataURL('image/png');
      this.$emit('change', { base64: this.drawBase64, type: 'draw' });
    },

    setDrawColor(color) {
      this.drawColor = color;
      if (this.drawCtx) this.drawCtx.strokeStyle = color;
    },

    clearDraw() {
      if (this.drawCtx) {
        const c = this.$refs.drawCanvas;
        if (c) this.drawCtx.clearRect(0, 0, c.width, c.height);
      }
      this.isDrawEmpty = true;
      this.drawBase64  = null;
      this.$emit('change', null);
    },

    // type tab
    initTypeCanvas() {
      const canvas = this.$refs.typeCanvas;
      if (!canvas) return;
      const rect    = canvas.parentElement.getBoundingClientRect();
      canvas.width  = rect.width || 700;
      canvas.height = 360;
      this.typeCtx  = canvas.getContext('2d');
    },

    setTypeColor(color) {
      this.typeColor = color;
      this.renderTypedSignature();
      this.autoSaveTyped();
    },

    renderTypedSignature() {
      const canvas = this.$refs.typeCanvas;
      const ctx    = this.typeCtx;
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!this.typedName) return;

      let fontSize = 72;
      ctx.font = `${fontSize}px ${this.selectedFontFamily}`;
      while (ctx.measureText(this.typedName).width > canvas.width - 40 && fontSize > 20) {
        fontSize -= 2;
        ctx.font = `${fontSize}px ${this.selectedFontFamily}`;
      }
      ctx.fillStyle    = this.typeColor;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.typedName, canvas.width / 2, canvas.height / 2);
    },

    onTypeInput() {
      this.renderTypedSignature();
      if (this.typeDebounceTimer) clearTimeout(this.typeDebounceTimer);
      this.typeDebounceTimer = setTimeout(() => this.autoSaveTyped(), 400);
    },

    autoSaveTyped() {
      if (!this.typedName.trim()) {
        this.typeBase64 = null;
        this.$emit('change', null);
        return;
      }
      this.showError = false;
      this.$nextTick(() => {
        const canvas = this.$refs.typeCanvas;
        if (!canvas) return;
        this.typeBase64 = canvas.toDataURL('image/png');
        this.$emit('change', { base64: this.typeBase64, type: 'type' });
      });
    },

    clearType() {
      this.typedName  = '';
      this.typeBase64 = null;
      if (this.typeCtx) {
        const c = this.$refs.typeCanvas;
        if (c) this.typeCtx.clearRect(0, 0, c.width, c.height);
      }
      this.$emit('change', null);
    },

    //Upload tab
    handleDrop(e) {
      this.isDragOver = false;
      const file = e.dataTransfer.files[0];
      if (file) this.processFile(file);
    },

    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) this.processFile(file);
      e.target.value = '';
    },

    processFile(file) {
      this.uploadError = '';
      const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!allowed.includes(file.type)) {
        this.uploadError = 'Only PNG, JPG, or SVG files are accepted.';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.uploadError = 'File size must be under 5MB.';
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.uploadPreview = ev.target.result;
        this.uploadBase64  = ev.target.result;
        this.uploadFile    = file;
        this.showError     = false;
        this.$emit('change', { base64: ev.target.result, type: 'upload', file });
      };
      reader.onerror = () => { this.uploadError = 'Failed to read file.'; };
      reader.readAsDataURL(file);
    },

    clearUpload() {
      this.uploadPreview = null;
      this.uploadBase64  = null;
      this.uploadFile    = null;
      this.uploadError   = '';
      this.$emit('change', null);
    },

    // fonts
    loadFonts() {
      this.computedFontArray.forEach(f => {
        if (!document.querySelector(`link[href="${f.value}"]`)) {
          const link = document.createElement('link');
          link.rel  = 'stylesheet';
          link.href = f.value;
          document.head.appendChild(link);
        }
      });
    },

    initDefaultFont() {
      if (this.computedFontArray.length) {
        const first             = this.computedFontArray[0];
        this.selectedFont       = first.key;
        this.selectedFontFamily = first.family;
      }
    },

    selectFontFromDropdown(font) {
      this.selectedFont       = font.key;
      this.selectedFontFamily = font.family;
      this.fontDropdownOpen   = false;
      this.renderTypedSignature();
      this.autoSaveTyped();
    },

    closeDropdownOutside(e) {
      const el = this.$refs.fontDropdown;
      if (el && !el.contains(e.target)) this.fontDropdownOpen = false;
    },

    onResize() {
      this.$nextTick(() => {
        if (this.activeTab === 'draw') this.initDrawCanvas();
        if (this.activeTab === 'type') { this.initTypeCanvas(); this.renderTypedSignature(); }
      });
    },

    isEmpty()        { return !this.activeSignatureBase64; },
    toDataURL()      { return this.activeSignatureBase64; },
    getType()        { return this.activeSignatureType; },
    getUploadFile()  { return this.activeSignatureFile; },

    clearAll() {
      this.clearTabData('draw');
      this.clearTabData('type');
      this.clearTabData('upload');
      this.$emit('change', null);
    },

    validate() {
      if (!this.activeSignatureBase64) { this.showError = true; return false; }
      this.showError = false;
      return true;
    },

    getSubmitPayload() {
      const base64  = this.activeSignatureBase64;
      const type    = this.activeSignatureType;
      const file    = this.activeSignatureFile;
      const isValid = !!base64;
      if (!isValid) this.showError = true;
      return { base64, type, file, isValid };
    },
  },
};
</script>

<style scoped>
.signature-maker {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  width: 100%;
}

.sig-tabs {
  display: flex;
  border-radius: 8px;
  background: #f0f4f8;
  padding: 4px;
  width: fit-content;
  margin-bottom: 16px;
}

.sig-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #718096;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.sig-tab:hover:not(.sig-tab--active) {
  color: #2d3748;
  background: rgba(255, 255, 255, 0.5);
}

.sig-tab--active {
  background: #0d9488;
  color: #fff;
  box-shadow: 0 1px 4px rgba(13, 148, 136, 0.3);
}

.sig-tab__icon {
  display: flex;
  align-items: center;
}

.sig-panel {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: visible;
  background: #fff;
}

.sig-canvas-wrap {
  position: relative;
  width: 100%;
  min-height: 180px;
  height: 360px;
}

.sig-canvas {
  display: block;
  width: 100%;
  height: 360px;
  cursor: crosshair;
  touch-action: none;
}

.sig-canvas-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e0;
  font-size: 0.9rem;
  pointer-events: none;
  user-select: none;
}

.sig-type-controls {
  padding: 14px 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.sig-type-input {
  flex: 1;
  min-width: 0;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  color: #2d3748;
  outline: none;
  transition: border-color 0.15s;
  margin-bottom: 12px;
}

.sig-type-input:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
}

.sig-font-dropdown {
  position: relative;
  flex-shrink: 0;
  margin-bottom: 12px;
}

.sig-font-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 0.875rem;
  color: #2d3748;
  transition: border-color 0.15s;
  white-space: nowrap;
  min-width: 165px;
  justify-content: space-between;
}

.sig-font-trigger:hover {
  border-color: #0d9488;
}

.sig-font-trigger__preview {
  font-size: 1.3rem;
  line-height: 1;
  min-width: 26px;
  text-align: center;
}

.sig-font-trigger__name {
  flex: 1;
  text-align: left;
  font-size: 0.8rem;
  color: #4a5568;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sig-font-trigger__caret {
  transition: transform 0.2s;
  color: #a0aec0;
  flex-shrink: 0;
}

.sig-font-trigger__caret--open {
  transform: rotate(180deg);
}

.sig-font-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 1000;
  min-width: 210px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 260px;
  overflow-y: auto;
}

.sig-font-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.12s;
}

.sig-font-option:hover {
  background: #f0fdf9;
}

.sig-font-option--active {
  background: #f0fdf9;
  color: #0d9488;
}

.sig-font-option__sample {
  font-size: 1.5rem;
  line-height: 1;
  min-width: 32px;
  text-align: center;
  color: #2d3748;
}

.sig-font-option__name {
  flex: 1;
  font-size: 0.85rem;
  color: #4a5568;
}

.sig-font-option__check {
  color: #0d9488;
  flex-shrink: 0;
}

.sig-dropdown-fade-enter-active,
.sig-dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.sig-dropdown-fade-enter,
.sig-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.sig-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #f0f4f8;
  background: #fafafa;
  flex-wrap: wrap;
  gap: 10px;
}

.sig-footer--upload {
  justify-content: flex-end;
}

.sig-color-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sig-color-label {
  font-size: 0.8rem;
  color: #718096;
  font-weight: 500;
}

.sig-color-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2.5px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s, border-color 0.15s;
}

.sig-color-dot:hover {
  transform: scale(1.15);
}

.sig-color-dot--active {
  border-color: #fff;
  box-shadow: 0 0 0 2px #0d9488;
  transform: scale(1.1);
}

.sig-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sig-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.15s;
  line-height: 1;
}

.sig-btn--ghost {
  background: transparent;
  border-color: #e2e8f0;
  color: #4a5568;
}

.sig-btn--ghost:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.sig-dropzone {
  margin: 16px;
  border: 2px dashed #cbd5e0;
  border-radius: 8px;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  position: relative;
  overflow: hidden;
}

.sig-dropzone:hover,
.sig-dropzone--active {
  border-color: #0d9488;
  background: #f0fdf9;
}

.sig-dropzone--has-file {
  cursor: default;
  border-style: solid;
  border-color: #0d9488;
}

.sig-dropzone__icon {
  color: #a0aec0;
  margin-bottom: 10px;
}

.sig-dropzone__text {
  font-size: 0.9rem;
  color: #4a5568;
  margin: 0 0 4px;
}

.sig-dropzone__link {
  color: #0d9488;
  font-weight: 600;
  text-decoration: underline;
}

.sig-dropzone__hint {
  font-size: 0.78rem;
  color: #a0aec0;
  margin: 0;
}

.sig-upload-preview {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
  border-radius: 4px;
}

.sig-upload-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
}

.sig-upload-remove:hover {
  background: rgba(0, 0, 0, 0.8);
}

.sig-upload-error {
  color: #e53e3e;
  font-size: 0.8rem;
  padding: 0 16px 8px;
}

.sig-validation-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  color: #e53e3e;
  font-size: 0.85rem;
}
</style>